export type LogLevel = 'NONE' | 'MINIMAL' | 'DETAILED';

export interface LogContext {
  module?: string;
  action?: string;
  userId?: string;
  sessionId?: string;
  [key: string]: any;
}

class Logger {
  private level: LogLevel;
  private isProduction: boolean;

  constructor() {
    // Get log level from environment with fallbacks
    const envLevel = (import.meta.env.VITE_LOG_LEVEL || '').toUpperCase() as LogLevel;
    this.isProduction = import.meta.env.PROD;
    
    // Default to DETAILED in development, MINIMAL in production
    this.level = this.isValidLogLevel(envLevel) 
      ? envLevel 
      : this.isProduction ? 'MINIMAL' : 'DETAILED';
  }

  private isValidLogLevel(level: string): level is LogLevel {
    return ['NONE', 'MINIMAL', 'DETAILED'].includes(level);
  }

  private shouldLog(requiredLevel: LogLevel): boolean {
    if (this.level === 'NONE') return false;
    if (this.level === 'MINIMAL') return ['MINIMAL'].includes(requiredLevel);
    if (this.level === 'DETAILED') return ['MINIMAL', 'DETAILED'].includes(requiredLevel);
    return false;
  }

  private formatMessage(level: string, message: string, context?: LogContext): string {
    const emoji = this.getLevelEmoji(level);
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    
    let formatted = `${emoji} [${timestamp}] ${message}`;
    
    if (context) {
      const contextStr = Object.entries(context)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => `${key}=${JSON.stringify(value)}`)
        .join(' ');
      
      if (contextStr) {
        formatted += ` | ${contextStr}`;
      }
    }
    
    return formatted;
  }

  private getLevelEmoji(level: string): string {
    const emojis: Record<string, string> = {
      'LOG': '📝',
      'INFO': 'ℹ️',
      'WARN': '⚠️',
      'ERROR': '❌',
      'DEBUG': '🔍',
      'SUCCESS': '✅',
      'API': '🌐',
      'UI': '🎨',
      'AUTH': '🔐',
      'DB': '💾',
      'WS': '🔌'
    };
    return emojis[level] || '📝';
  }

  // Core logging methods
  log(message: string, context?: LogContext): void {
    if (this.shouldLog('DETAILED')) {
      console.log(this.formatMessage('LOG', message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog('DETAILED')) {
      console.info(this.formatMessage('INFO', message, context));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog('MINIMAL')) {
      console.warn(this.formatMessage('WARN', message, context));
    }
  }

  error(message: string, error?: any, context?: LogContext): void {
    if (this.shouldLog('MINIMAL')) {
      const errorContext = { ...context };
      if (error) {
        errorContext.error = error.message || error;
        if (error.stack) errorContext.stack = error.stack;
      }
      console.error(this.formatMessage('ERROR', message, errorContext));
    }
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog('DETAILED')) {
      console.debug(this.formatMessage('DEBUG', message, context));
    }
  }

  success(message: string, context?: LogContext): void {
    if (this.shouldLog('DETAILED')) {
      console.log(this.formatMessage('SUCCESS', message, context));
    }
  }

  // Specialized logging methods for common patterns
  logApiCall(method: string, url: string, status?: number, duration?: number): void {
    if (this.shouldLog('DETAILED')) {
      const context: LogContext = { 
        module: 'API', 
        method, 
        url,
        status,
        duration: duration ? `${duration}ms` : undefined
      };
      console.log(this.formatMessage('API', `${method} ${url}${status ? ` - ${status}` : ''}`, context));
    }
  }

  logStateChange(component: string, oldState: any, newState: any): void {
    if (this.shouldLog('DETAILED')) {
      const context: LogContext = { 
        module: 'UI', 
        component,
        oldState: JSON.stringify(oldState),
        newState: JSON.stringify(newState)
      };
      console.log(this.formatMessage('UI', `State change in ${component}`, context));
    }
  }

  logAuth(action: string, userId?: string, result?: 'success' | 'failure'): void {
    const level = result === 'failure' ? 'MINIMAL' : 'DETAILED';
    if (this.shouldLog(level)) {
      const context: LogContext = { module: 'AUTH', action, userId, result };
      const emoji = result === 'failure' ? 'ERROR' : 'AUTH';
      console.log(this.formatMessage(emoji, `Auth: ${action}`, context));
    }
  }

  logWebSocket(action: string, event?: string, data?: any): void {
    if (this.shouldLog('DETAILED')) {
      const context: LogContext = { 
        module: 'WebSocket', 
        action, 
        event,
        data: data ? JSON.stringify(data) : undefined
      };
      console.log(this.formatMessage('WS', `WebSocket: ${action}`, context));
    }
  }

  logDatabase(action: string, table?: string, query?: string, duration?: number): void {
    if (this.shouldLog('DETAILED')) {
      const context: LogContext = { 
        module: 'Database', 
        action, 
        table,
        query,
        duration: duration ? `${duration}ms` : undefined
      };
      console.log(this.formatMessage('DB', `DB: ${action}`, context));
    }
  }

  // Runtime control methods
  setLevel(level: LogLevel): void {
    if (this.isValidLogLevel(level)) {
      this.level = level;
      this.info(`Log level changed to ${level}`);
    } else {
      this.warn(`Invalid log level: ${level}. Valid levels: NONE, MINIMAL, DETAILED`);
    }
  }

  getLevel(): LogLevel {
    return this.level;
  }

  // Method to check if a specific level would log
  wouldLog(level: LogLevel): boolean {
    return this.shouldLog(level);
  }
}

// Create singleton instance
export const logger = new Logger();

// Export convenience functions for backward compatibility
export const logInfo = (message: string, context?: LogContext) => logger.info(message, context);
export const logError = (message: string, error?: any, context?: LogContext) => logger.error(message, error, context);
export const logWarn = (message: string, context?: LogContext) => logger.warn(message, context);
export const logDebug = (message: string, context?: LogContext) => logger.debug(message, context);
export const logSuccess = (message: string, context?: LogContext) => logger.success(message, context);

// Specialized logging functions
export const logApiCall = (method: string, url: string, status?: number, duration?: number) => 
  logger.logApiCall(method, url, status, duration);

export const logStateChange = (component: string, oldState: any, newState: any) => 
  logger.logStateChange(component, oldState, newState);

export const logAuth = (action: string, userId?: string, result?: 'success' | 'failure') => 
  logger.logAuth(action, userId, result);

export const logWebSocket = (action: string, event?: string, data?: any) => 
  logger.logWebSocket(action, event, data);

export const logDatabase = (action: string, table?: string, query?: string, duration?: number) => 
  logger.logDatabase(action, table, query, duration);